import React, {
  useMemo,
  createElement,
  Component as ReactComponent,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { Handle, Position, NodeResizer, type NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  getComponentIcon,
  getComponentIconColor,
} from '../../config/component-icons';
import { RISK_LEVEL_COLORS } from '../../theme/theme';

// Error boundary for catching render errors in nodes
class NodeErrorBoundary extends ReactComponent<
  { children: ReactNode; nodeId: string },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; nodeId: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      `[CustomNode Error] Node ${this.props.nodeId}:`,
      error,
      errorInfo
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <Paper
          sx={{ p: 1, backgroundColor: '#ffebee', border: '1px solid red' }}
        >
          <Typography variant="caption" color="error">
            Error: {this.state.error?.message}
          </Typography>
        </Paper>
      );
    }
    return this.props.children;
  }
}

export interface CustomNodeData {
  label: string;
  type: string;
  componentId: string;
  // CIA scores (1-10)
  confidentialityScore?: number;
  integrityScore?: number;
  availabilityScore?: number;
  // Risk info
  highestRiskScore?: number; // Highest residual risk score
  riskCount?: number; // Number of risks
  // Connection state - when true, show handles on all nodes
  isConnecting?: boolean;
}

// Handle style helper - handles are hidden by default, shown when selected or connecting
const createHandleStyle = (
  color: string,
  isVisible = false,
  isSecondary = false
) => ({
  background: color,
  width: isVisible ? 8 : 4,
  height: isVisible ? 8 : 4,
  border: isVisible ? '2px solid #fff' : 'none',
  opacity: isVisible ? 1 : 0,
  transition: 'all 0.2s ease-in-out',
  pointerEvents: 'all' as const,
  // Secondary handles (hidden duplicates on same side) stay invisible
  ...(isSecondary && { opacity: 0, width: 4, height: 4 }),
});

// Get color based on CIA score (1-10)
const getCIAScoreColor = (score?: number | string): string => {
  if (score === undefined || score === null) return '#E0E0E0'; // Grey for no data
  const numScore =
    typeof score === 'number' ? score : parseFloat(String(score));
  if (isNaN(numScore)) return '#E0E0E0'; // Grey for invalid data
  if (numScore >= 8) return RISK_LEVEL_COLORS.critical; // High sensitivity - red
  if (numScore >= 6) return RISK_LEVEL_COLORS.high; // Medium-high - purple
  if (numScore >= 4) return RISK_LEVEL_COLORS.medium; // Medium - yellow
  if (numScore >= 2) return RISK_LEVEL_COLORS.low; // Low - teal
  return RISK_LEVEL_COLORS.minimal; // Minimal - grey
};

// Get risk level from score
const getRiskLevel = (
  score?: number
): 'none' | 'low' | 'medium' | 'high' | 'critical' => {
  if (score === undefined || score === null) return 'none';
  if (score >= 7) return 'critical';
  if (score >= 5) return 'high';
  if (score >= 3) return 'medium';
  if (score > 0) return 'low';
  return 'none';
};

/**
 * CIA Indicator - 3 small colored dots showing Confidentiality, Integrity, Availability
 */
const CIAIndicator: React.FC<{
  confidentiality?: number;
  integrity?: number;
  availability?: number;
}> = ({ confidentiality, integrity, availability }) => {
  const hasData =
    confidentiality !== undefined ||
    integrity !== undefined ||
    availability !== undefined;
  if (!hasData) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 4,
        left: 4,
        display: 'flex',
        gap: '3px',
        zIndex: 10,
      }}
    >
      <Tooltip title={`C: ${confidentiality ?? '-'}`} arrow placement="top">
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: getCIAScoreColor(confidentiality),
            border: '1px solid rgba(255,255,255,0.8)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        />
      </Tooltip>
      <Tooltip title={`I: ${integrity ?? '-'}`} arrow placement="top">
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: getCIAScoreColor(integrity),
            border: '1px solid rgba(255,255,255,0.8)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        />
      </Tooltip>
      <Tooltip title={`A: ${availability ?? '-'}`} arrow placement="top">
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: getCIAScoreColor(availability),
            border: '1px solid rgba(255,255,255,0.8)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        />
      </Tooltip>
    </Box>
  );
};

/**
 * Risk Indicator - Shows risk status with icon
 */
const RiskIndicator: React.FC<{
  highestRiskScore?: number;
  riskCount?: number;
}> = ({ highestRiskScore, riskCount }) => {
  // Ensure highestRiskScore is a number
  const score =
    typeof highestRiskScore === 'number'
      ? highestRiskScore
      : typeof highestRiskScore === 'string'
        ? parseFloat(highestRiskScore)
        : undefined;

  const riskLevel = getRiskLevel(score);

  // Don't show anything if no risks
  if (riskLevel === 'none' && (!riskCount || riskCount === 0)) return null;

  const getIndicator = () => {
    const scoreDisplay = score !== undefined ? score.toFixed(1) : '?';
    switch (riskLevel) {
      case 'critical':
        return {
          icon: ErrorIcon,
          color: RISK_LEVEL_COLORS.critical,
          tooltip: `Critical risk (${scoreDisplay})`,
        };
      case 'high':
        return {
          icon: ErrorIcon,
          color: RISK_LEVEL_COLORS.high,
          tooltip: `High risk (${scoreDisplay})`,
        };
      case 'medium':
        return {
          icon: WarningIcon,
          color: RISK_LEVEL_COLORS.medium,
          tooltip: `Medium risk (${scoreDisplay})`,
        };
      case 'low':
        return {
          icon: CheckCircleIcon,
          color: RISK_LEVEL_COLORS.low,
          tooltip: `Low risk (${scoreDisplay})`,
        };
      default:
        return {
          icon: CheckCircleIcon,
          color: RISK_LEVEL_COLORS.minimal,
          tooltip: 'No significant risks',
        };
    }
  };

  const indicator = getIndicator();
  const IconComp = indicator.icon;

  return (
    <Tooltip title={indicator.tooltip} arrow placement="top">
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: 2,
          right: 4,
          zIndex: 10,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px',
        }}
      >
        <IconComp sx={{ fontSize: 14, color: indicator.color }} />
        {riskCount && riskCount > 1 && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              fontWeight: 600,
              color: indicator.color,
              lineHeight: 1,
            }}
          >
            {riskCount}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

/**
 * Custom ReactFlow node component for CMDB entities
 * Displays component with appropriate icon and brand colors
 * Has handles on all 4 sides for flexible connections
 * Handles are hidden by default and shown when selected or when user is connecting
 */
const CustomNodeInner: React.FC<NodeProps> = ({ data, selected }) => {
  const nodeData = (data || {}) as unknown as CustomNodeData;

  // Handles should be visible when node is selected OR when user is dragging a connection
  const showHandles = selected || nodeData.isConnecting;

  // Debug logging
  console.info('[CustomNode] Rendering node:', {
    componentId: nodeData.componentId,
    label: nodeData.label,
    type: nodeData.type,
    confidentialityScore: nodeData.confidentialityScore,
    integrityScore: nodeData.integrityScore,
    availabilityScore: nodeData.availabilityScore,
    highestRiskScore: nodeData.highestRiskScore,
    riskCount: nodeData.riskCount,
    isConnecting: nodeData.isConnecting,
    showHandles,
  });

  const iconColor = useMemo(
    () => getComponentIconColor(nodeData.type || 'default'),
    [nodeData.type]
  );
  const IconComponent = useMemo(
    () => getComponentIcon(nodeData.type || 'default'),
    [nodeData.type]
  );

  return (
    <>
      {/* NodeResizer for resizing regular nodes when selected */}
      <NodeResizer
        color={iconColor}
        isVisible={selected}
        minWidth={120}
        minHeight={60}
        handleStyle={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: iconColor,
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
        lineStyle={{
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: iconColor,
        }}
      />
      <Paper
        elevation={2}
        sx={{
          padding: 1.5,
          paddingTop: 2.5, // Extra padding for indicators
          minWidth: 150,
          // Use consistent 2px border to prevent size changes on selection
          border: `2px solid ${selected ? iconColor : '#E0E0E0'}`,
          borderRadius: 1,
          backgroundColor: '#FFFFFF',
          cursor: 'pointer',
          transition: 'border-color 0.2s ease-in-out',
          position: 'relative',
          // Use box-shadow for selection highlight instead of elevation change
          boxShadow: selected
            ? `0 0 0 2px ${iconColor}40, 0 4px 12px rgba(0,0,0,0.15)`
            : undefined,
          '&:hover': {
            borderColor: selected ? iconColor : '#BDBDBD',
          },
        }}
      >
        {/* CIA Indicator - top left */}
        <CIAIndicator
          confidentiality={nodeData.confidentialityScore}
          integrity={nodeData.integrityScore}
          availability={nodeData.availabilityScore}
        />

        {/* Risk Indicator - top right */}
        <RiskIndicator
          highestRiskScore={nodeData.highestRiskScore}
          riskCount={nodeData.riskCount}
        />

        {/* TOP handles */}
        <Handle
          type="target"
          position={Position.Top}
          id="top-target"
          style={createHandleStyle(iconColor, showHandles)}
        />
        <Handle
          type="source"
          position={Position.Top}
          id="top-source"
          style={createHandleStyle(iconColor, showHandles, true)}
        />

        {/* BOTTOM handles */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom-source"
          style={createHandleStyle(iconColor, showHandles)}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom-target"
          style={createHandleStyle(iconColor, showHandles, true)}
        />

        {/* LEFT handles */}
        <Handle
          type="target"
          position={Position.Left}
          id="left-target"
          style={createHandleStyle(iconColor, showHandles)}
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left-source"
          style={createHandleStyle(iconColor, showHandles, true)}
        />

        {/* RIGHT handles */}
        <Handle
          type="source"
          position={Position.Right}
          id="right-source"
          style={createHandleStyle(iconColor, showHandles)}
        />
        <Handle
          type="target"
          position={Position.Right}
          id="right-target"
          style={createHandleStyle(iconColor, showHandles, true)}
        />

        {/* Node content */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {createElement(IconComponent, {
            size: 24,
            color: iconColor,
            strokeWidth: 2,
          })}
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500, // Keep consistent to prevent size changes
                color: '#292929', // Avinor koksgra
                wordBreak: 'break-word',
              }}
            >
              {nodeData.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#939597', // Avinor grå 04
                textTransform: 'capitalize',
              }}
            >
              {nodeData.type.replace(/_/g, ' ')}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

// Export with error boundary wrapper
export const CustomNode: React.FC<NodeProps> = props => {
  const nodeId = String(props.data?.componentId || props.id || 'unknown');
  return (
    <NodeErrorBoundary nodeId={nodeId}>
      <CustomNodeInner {...props} />
    </NodeErrorBoundary>
  );
};

/**
 * Zone/Group node component for security zones
 * Visual container with labeled border - no icon, no relations shown
 * Child nodes are positioned inside and constrained to zone bounds
 * Resizable when selected
 */
export const ZoneNode: React.FC<NodeProps> = ({ data, selected }) => {
  const nodeData = (data || {}) as unknown as CustomNodeData;

  // Determine zone color based on zone name/type
  const getZoneColor = (label: string) => {
    const normalized = label.toLowerCase();
    if (normalized.includes('dmz') || normalized.includes('public')) {
      return '#FF5252'; // Coral - exposed/public
    }
    if (normalized.includes('internal') || normalized.includes('private')) {
      return '#00D9FF'; // Cyan - internal
    }
    if (normalized.includes('secure') || normalized.includes('restricted')) {
      return '#2D3B45'; // Steel - high security
    }
    if (normalized.includes('management') || normalized.includes('admin')) {
      return '#FFB300'; // Amber - management
    }
    return '#9AA0A6'; // Slate - default
  };

  const zoneColor = getZoneColor(nodeData.label || '');

  return (
    <>
      {/* NodeResizer for resizing zones */}
      <NodeResizer
        color={zoneColor}
        isVisible={selected}
        minWidth={200}
        minHeight={150}
        handleStyle={{
          width: 10,
          height: 10,
          borderRadius: 2,
        }}
        lineStyle={{
          borderWidth: 2,
          borderStyle: 'dashed',
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          padding: 0,
          minWidth: 200,
          minHeight: 150,
          border: selected
            ? `3px dashed ${zoneColor}`
            : `2px dashed ${zoneColor}`,
          borderRadius: 3,
          backgroundColor: `${zoneColor}05`, // Very light tint (5% opacity)
          position: 'relative',
          cursor: 'move',
          transition: 'border-color 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: `${zoneColor}08`,
          },
        }}
      >
        {/* Zone label badge - top left corner */}
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: 16,
            padding: '4px 12px',
            backgroundColor: '#FFFFFF',
            border: `2px solid ${zoneColor}`,
            borderRadius: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 10,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: zoneColor,
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: '0.7rem',
            }}
          >
            {nodeData.label}
          </Typography>
        </Box>

        {/* Optional: Grid pattern background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(${zoneColor}15 1px, transparent 1px),
              linear-gradient(90deg, ${zoneColor}15 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
            borderRadius: 3,
            pointerEvents: 'none',
          }}
        />
      </Box>
    </>
  );
};
